import { LoadingContainer } from 'peach/components'
import { useParams, useHistory } from 'peach/router'

import { DynamicForm, Page, PageHeader } from 'core/components'
import { useGenerateMetro2Files } from 'core/types'
import type {
  GenerateMetro2Files,
  AgencyName,
} from 'core/types/generated/credit-agencies/types'
import { addToast } from 'core/stores/toasts'

const GenerateCreditReport = () => {
  const history = useHistory()
  const { companyId } = useParams<{ companyId?: string }>()

  const { mutateAsync: generateMetro2Files } = useGenerateMetro2Files({
    companyId,
  })

  const initial: GenerateMetro2Files & { preventUpload?: boolean } = {
    reportDate: '',
    agencyNames: [],
    preventUpload: false,
  }

  return (
    <LoadingContainer isLoading={false}>
      <Page variant='full'>
        <PageHeader title='Generate Credit Report' />
        <DynamicForm
          data={initial}
          fieldEditMode='all'
          fieldOrder={['reportDate', 'agencyNames', 'preventUpload']}
          fieldOverrides={{
            agencyNames: 'MultiSelect',
          }}
          onSubmit={async (payload: any) => {
            const body = payload as GenerateMetro2Files & {
              preventUpload?: boolean
            }

            // Validate report date (defensive check, validationRules also handle this)
            if (
              !body.reportDate ||
              typeof body.reportDate !== 'string' ||
              body.reportDate.trim().length === 0
            ) {
              addToast('Report date is required', 'error')
              return
            }

            // Validate and filter agency names
            const validAgencyNames: Array<AgencyName> = [
              'experian',
              'equifax',
              'transUnion',
              'innovis',
            ]
            let agencyNames: Array<AgencyName> = []

            if (Array.isArray(body.agencyNames)) {
              agencyNames = body.agencyNames.filter(
                (name): name is AgencyName =>
                  typeof name === 'string' &&
                  validAgencyNames.includes(name as AgencyName),
              )
            } else if (body.agencyNames) {
              // Handle case where it might be a single value
              const name = body.agencyNames
              if (
                typeof name === 'string' &&
                validAgencyNames.includes(name as AgencyName)
              ) {
                agencyNames = [name as AgencyName]
              }
            }

            // Defensive check: ensure at least one valid agency is selected
            // This handles edge cases where validationRules might not catch malformed data
            if (agencyNames.length === 0) {
              addToast('Please select at least one valid agency', 'error')
              return
            }

            try {
              const response = await generateMetro2Files(
                {
                  reportDate: body.reportDate,
                  agencyNames,
                },
                {
                  query: body.preventUpload
                    ? { preventUpload: true }
                    : undefined,
                },
              )

              const resultData = response.data
              const messagesSent = resultData?.messagesSent?.length || 0
              const messagesNotSent = resultData?.messagesNotSent?.length || 0

              if (messagesSent > 0 && messagesNotSent === 0) {
                addToast(
                  `Successfully generated Metro2 files for ${messagesSent} ${
                    messagesSent === 1 ? 'agency' : 'agencies'
                  }`,
                  'success',
                )
              } else if (messagesNotSent > 0) {
                addToast(
                  `Generated files for ${messagesSent} ${
                    messagesSent === 1 ? 'agency' : 'agencies'
                  }, but failed for ${messagesNotSent} ${
                    messagesNotSent === 1 ? 'agency' : 'agencies'
                  }`,
                  'warning',
                )
              } else if (messagesSent === 0 && messagesNotSent === 0) {
                addToast(
                  'Request completed, but no messages were sent or failed',
                  'warning',
                )
              }

              history.push(`/companies/${companyId}/credit-agencies`)
            } catch (error: any) {
              addToast(
                error?.message || 'Failed to generate credit report',
                'error',
              )
            }
          }}
          selectOptions={{
            agencyNames: [
              { label: 'Experian', value: 'experian' },
              { label: 'Equifax', value: 'equifax' },
              { label: 'TransUnion', value: 'transUnion' },
              { label: 'Innovis', value: 'innovis' },
            ],
          }}
          submitText='Generate Credit Report'
          validationRules={{
            reportDate: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'Report date is required',
            ],
            agencyNames: [
              (v: unknown) =>
                (Array.isArray(v) && v.length > 0) ||
                'At least one agency must be selected',
            ],
          }}
        />
      </Page>
    </LoadingContainer>
  )
}

export default GenerateCreditReport
